import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner, Tab, Tabs, Table, Card } from 'react-bootstrap';
import { UserPlus, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';

interface ManageOrgModalProps {
    show: boolean;
    onHide: () => void;
    orgId: string;
    onSuccess: () => void;
}

interface OrganizationDetails {
    id: string;
    name: string;
    domain: string;
    status: string;
    type?: string;
}

// Updated to include role
interface OrgUser {
    id: string;
    email: string;
    full_name: string;
    last_login: string;
    role: string;
    status: string;
}

interface PasswordResetResponse {
    temporaryPasswordSet?: boolean;
    token?: string;
    message?: string;
}

interface UserInviteFormData {
    email: string;
    fullName: string;
    role: 'user' | 'admin';
}

type FormControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const ManageOrgModal: React.FC<ManageOrgModalProps> = ({ show, onHide, orgId, onSuccess }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [orgDetails, setOrgDetails] = useState<OrganizationDetails | null>(null);
    const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
    const [resettingPassword, setResettingPassword] = useState<string | null>(null);
    const [invitingUser, setInvitingUser] = useState(false);

    // User invite form state
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteFormData, setInviteFormData] = useState<UserInviteFormData>({
        email: '',
        fullName: '',
        role: 'user',
    });
    const [emailError, setEmailError] = useState('');

    const fetchOrgDetails = async () => {
        try {
            const data = await apiService.get<OrganizationDetails>(
                `${API_ENDPOINTS.ORGANIZATIONS.GET}/${orgId}`
            );
            setOrgDetails(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            throw err;
        }
    };

    const fetchOrgUsers = async () => {
        try {
            const data = await apiService.get<OrgUser[]>(
                `${API_ENDPOINTS.ORGANIZATIONS.GET_USERS}/${orgId}/users`
            );
            setOrgUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setOrgUsers([]);
            throw err;
        }
    };

    useEffect(() => {
        if (show && orgId) {
            setLoading(true);
            setError('');
            setSuccess('');

            // Reset the invite form when modal is opened
            setShowInviteForm(false);
            setInviteFormData({
                email: '',
                fullName: '',
                role: 'user',
            });
            setEmailError('');

            // Always fetch org details and all users
            Promise.all([
                fetchOrgDetails(),
                fetchOrgUsers(),
            ])
                .catch((err) => {
                    console.error('Error fetching data:', err);
                    setError('Failed to load organization data');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [show, orgId]);

    const handleChange = (e: React.ChangeEvent<FormControlElement>) => {
        const { name, value } = e.target;
        setOrgDetails(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleResetPassword = async (userId: string) => {
        try {
            const temporaryPassword = window.prompt('Enter temporary password (optional). Leave empty to only send reset link:');

            // If user clicks cancel, abort the operation
            if (temporaryPassword === null) {
                return;
            }

            setResettingPassword(userId);
            setError('');
            setSuccess('');

            // Validate temporary password if provided
            if (temporaryPassword && temporaryPassword.length < 8) {
                setError('Temporary password must be at least 8 characters long');
                setResettingPassword(null);
                return;
            }

            const data = await apiService.post<PasswordResetResponse>(
                API_ENDPOINTS.AUTH.RESET_PASSWORD,
                {
                    userId,
                    temporaryPassword: temporaryPassword || undefined
                }
            );

            let successMessage = 'Password reset initiated successfully.';
            if (data.temporaryPasswordSet) {
                successMessage += ' Temporary password has been set.';
            }
            if (data.token) {
                successMessage += ` Reset token: ${data.token}`;
            } else {
                successMessage += ' Reset link will be sent to user.';
            }

            setSuccess(successMessage);

            // Refresh the user list to show updated information
            await fetchOrgUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reset password');
            console.error('Password reset error:', err);
        } finally {
            setResettingPassword(null);
        }
    };

    const handleOrgSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!orgDetails) return;

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const data = await apiService.put<OrganizationDetails>(
                `${API_ENDPOINTS.ORGANIZATIONS.UPDATE}/${orgId}`,
                orgDetails
            );
            setSuccess('Organization updated successfully');
            onSuccess();
            setOrgDetails(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    // Check if this is a consumer organization to determine email domain validation
    const isConsumerOrg = () => {
        if (!orgDetails) return false;

        // Check for explicit type field first
        if (orgDetails.type === 'consumer') return true;

        // Fallback: Check if the domain includes 'consumer'
        return orgDetails.domain.toLowerCase().includes('consumer');
    };

    const handleInviteFormChange = (e: React.ChangeEvent<FormControlElement>) => {
        const { name, value } = e.target;
        setInviteFormData(prev => ({ ...prev, [name]: value }));

        // Clear email error when email is changed
        if (name === 'email') {
            setEmailError('');
        }
    };

    const validateInviteForm = (): boolean => {
        // Validate email format
        if (!inviteFormData.email || !inviteFormData.email.includes('@')) {
            setEmailError('Please enter a valid email address');
            return false;
        }

        // Only validate domain for non-consumer organizations
        if (!isConsumerOrg() && orgDetails?.domain) {
            const domain = orgDetails.domain.toLowerCase();
            if (!inviteFormData.email.toLowerCase().endsWith('@' + domain)) {
                setEmailError(`Email must be from the ${orgDetails.domain} domain`);
                return false;
            }
        }

        // Validate name
        if (!inviteFormData.fullName.trim()) {
            setError('Please enter a full name');
            return false;
        }

        return true;
    };

    // Updated to use the new system-invite-user endpoint
    const handleSubmitInvite = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateInviteForm()) {
            return;
        }

        setInvitingUser(true);
        setError('');
        setSuccess('');

        try {
            // Use the newly created system-invite-user endpoint
            await apiService.post(
                `${API_ENDPOINTS.ORGANIZATIONS.GET}/${orgId}/system-invite-user`,
                {
                    email: inviteFormData.email,
                    fullName: inviteFormData.fullName,
                    role: inviteFormData.role
                }
            );

            setSuccess(`Invitation sent to ${inviteFormData.email}`);

            // Reset form
            setInviteFormData({
                email: '',
                fullName: '',
                role: 'user'
            });

            // Hide the form after successful submission
            setShowInviteForm(false);

            // Refresh the list to show the new user
            await fetchOrgUsers();
        } catch (err) {
            // Updated error handling for the new endpoint
            if (err instanceof Error && err.message.includes('404')) {
                setError(`Backend endpoint not found. Please ensure you've added the system-invite-user endpoint.`);
            } else if (err instanceof Error && err.message.includes('403')) {
                setError(`You don't have permission to invite users to this organization, or this is not a consumer organization.`);
            } else {
                setError(err instanceof Error ? err.message : 'Failed to send invitation');
                console.error('Invitation error:', err);
            }
        } finally {
            setInvitingUser(false);
        }
    };

    // Alternative approach - handle user invitation directly
    const manualInvite = () => {
        // Display a success message with instructions for the admin
        setError('');
        setSuccess(`To add users to this organization:
        
1. Navigate to the Users page
2. Click "Invite User" 
3. Select this organization from the dropdown
4. Enter user details and send invitation

This approach ensures proper permissions and email delivery.`);
    };

    const formatDate = (dateString: string | undefined) => {
        return dateString ? new Date(dateString).toLocaleString() : 'Never';
    };

    const renderInviteForm = () => {
        if (!showInviteForm) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Invite New User</h5>
                    <Button
                        variant="link"
                        className="p-0 text-muted"
                        onClick={() => setShowInviteForm(false)}
                    >
                        <X size={20} />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmitInvite}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={inviteFormData.email}
                                onChange={handleInviteFormChange}
                                required
                                isInvalid={!!emailError}
                            />
                            {emailError ? (
                                <Form.Control.Feedback type="invalid">
                                    {emailError}
                                </Form.Control.Feedback>
                            ) : isConsumerOrg() ? (
                                <Form.Text className="text-muted">
                                    Any email domain is allowed for this consumer organization.
                                </Form.Text>
                            ) : orgDetails?.domain ? (
                                <Form.Text className="text-muted">
                                    Email must be from the {orgDetails.domain} domain.
                                </Form.Text>
                            ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="fullName"
                                value={inviteFormData.fullName}
                                onChange={handleInviteFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                name="role"
                                value={inviteFormData.role}
                                onChange={handleInviteFormChange}
                            >
                                <option value="user">Regular User</option>
                                <option value="admin">Admin</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                {inviteFormData.role === 'admin'
                                    ? 'Admins can manage organization resources and users.'
                                    : 'Regular users can only access resources shared with them.'}
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button
                                variant="secondary"
                                className="me-2"
                                onClick={() => setShowInviteForm(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={invitingUser}
                            >
                                {invitingUser ? (
                                    <>
                                        <Spinner size="sm" className="me-1" />
                                        Inviting...
                                    </>
                                ) : 'Send Invitation'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    const renderOrgUsers = () => {
        if (loading) return null;

        // Function to format role for display
        const formatRole = (role: string) => {
            switch (role) {
                case 'admin':
                    return 'Admin';
                case 'system_admin':
                    return 'System Admin';
                case 'user':
                default:
                    return 'User';
            }
        };

        return (
            <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Organization Users</h5>
                    {!showInviteForm && (
                        <div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={manualInvite}
                                className="me-2"
                            >
                                User Management Instructions
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowInviteForm(true)}
                                className="d-flex align-items-center gap-2"
                            >
                                <UserPlus size={16} />
                                Invite User
                            </Button>
                        </div>
                    )}
                </div>

                {renderInviteForm()}

                <Table responsive>
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {orgUsers.map((user) => (
                        <tr key={user.id}>
                            <td>{user.full_name}</td>
                            <td>{user.email}</td>
                            <td>{formatRole(user.role)}</td>
                            <td>{formatDate(user.last_login)}</td>
                            <td>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleResetPassword(user.id)}
                                    disabled={resettingPassword === user.id}
                                >
                                    {resettingPassword === user.id ? (
                                        <>
                                            <Spinner size="sm" className="me-1" />
                                            Resetting...
                                        </>
                                    ) : 'Reset Password'}
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {orgUsers.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-center py-3">
                                <em>No users found</em>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </>
        );
    };

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Manage Organization</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {loading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    )}

                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    {!loading && orgDetails && (
                        <Tabs defaultActiveKey="general" className="mb-3">
                            <Tab eventKey="general" title="General">
                                <Form onSubmit={handleOrgSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Organization Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={orgDetails.name}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Domain</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="domain"
                                            value={orgDetails.domain}
                                            onChange={handleChange}
                                        />
                                        {isConsumerOrg() && (
                                            <Form.Text className="text-muted">
                                                This is a consumer organization. Domain is for identification only and not enforced for user emails.
                                            </Form.Text>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={orgDetails.status}
                                            onChange={handleChange}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="suspended">Suspended</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Form>
                            </Tab>

                            <Tab eventKey="users" title="Users">
                                {renderOrgUsers()}
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleOrgSubmit}
                        disabled={loading || saving}
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageOrgModal;